/*
 * Ficha del empleado (ruta /marcaciones/empleado/:id, permiso view_attendance): se abre al tocar un
 * nombre en cualquier lista del tablero. Muestra, para el mes elegido (?mes=AAAA-MM, flechas para cambiar,
 * sin pasar del mes de hoy):
 * - los KPI de la persona con comparación contra el periodo anterior (GET /attendance/dashboard/ con
 *   id_empleado; se reusa KpiCard),
 * - las horas por semana (GET /attendance/horas/?agrupar=semana): total del mes (suma de jornadas completas),
 * - el historial día por día con la barra de jornada (GET /attendance/jornadas/ con id_empleado).
 * Con generate_report_attendance puede exportar sus jornadas del mes (seccion=jornadas + id_empleado).
 * "Volver a Marcaciones" regresa a la pantalla de origen (state.from que guarda EmployeeName) o a /marcaciones.
 * Notas: el id de la URL se valida como entero positivo (si no, error claro y no se llama al servidor);
 * mientras carga no se muestran datos de otro mes/persona (solo respuestas con status 'ok'); solo se
 * muestran nombre, cargo, tienda, zona y marcaciones. El nombre sale de sus jornadas: un mes sin marcas
 * muestra "Empleado sin marcaciones en <mes>".
 */
import React from "react";
import {useSelector} from "react-redux";
import {Link, useHistory, useLocation, useParams} from "react-router-dom";
import {ToastContainer} from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Breadcrumbs from "../../../components/breadcrumbs";
import {fetchDashboard, fetchHours, fetchWorkdays} from "../../../redux/attendance";
import useRemoteData from "./hooks/useRemoteData";
import StateView from "./components/StateView";
import StatTile from "./components/StatTile";
import StatusBadge from "./components/StatusBadge";
import KpiCard from "./components/KpiCard";
import ExportButton from "./components/ExportButton";
import BarraJornada, {TimelineAxis} from "./components/BarraJornada";
import {arrivalStatus, durationLabel, exitBadge, formatNumber, hhmm, hoursSummary} from "./utils/rows";
import {
    groupLabel, monthLabel, monthRange, parseEmployeeId, readMonth, shiftMonth, shortDayLabel, todayISO,
} from "./utils/period";
import "./attendance.css";

const EmployeeProfile = (props) => {
    const {id} = useParams();
    const location = useLocation();
    const history = useHistory();
    const login = useSelector((state) => state.users_module.login.data) || {};
    const token = login.token;
    const canExport = Object.values(props).some((permiso) => permiso && permiso.codigo === 'generate_report_attendance');

    const employeeId = parseEmployeeId(id);
    const today = todayISO();
    const month = readMonth(location.search, today);
    const range = monthRange(month);
    const params = {fecha_desde: range.desde, fecha_hasta: range.hasta, id_empleado: employeeId};
    const key = token && employeeId ? JSON.stringify(params) : null;
    const workdays = useRemoteData(() => fetchWorkdays(token, params), key);
    const hours = useRemoteData(() => fetchHours(token, {...params, agrupar: 'semana'}), key);
    const dashboard = useRemoteData(() => fetchDashboard(token, params), key);

    // useRemoteData conserva `data` anterior mientras carga: solo una respuesta recibida (ok) se presenta como actual.
    const rows = workdays.status === 'ok' ? workdays.data.datos : [];
    const person = rows[0];
    const dayRows = rows.slice().sort((a, b) => (a.fecha < b.fecha ? 1 : -1));
    const weeks = hours.status === 'ok' ? hours.data.datos : [];
    const totals = hoursSummary(weeks);
    const from = location.state && location.state.from ? location.state.from : '/marcaciones';
    const goToMonth = (step) => history.replace({
        pathname: location.pathname, search: `?mes=${shiftMonth(month, step)}`, state: location.state,
    });
    const title = person ? person.empleado
        : (workdays.status === 'ok' ? `Empleado sin marcaciones en ${monthLabel(month).toLowerCase()}` : 'Ficha del empleado');

    return (
        <div className="app-content content">
            <div className="content-overlay"/>
            <div className="content-wrapper mc-root">
                <div className="content-header row">
                    <div className="content-header-left col-12 mb-1 mt-1">
                        <Breadcrumbs title="Ficha del empleado"
                                     items={[{label: "Marcaciones", link: "/marcaciones"}, {label: "Ficha", link: location.pathname}]}/>
                    </div>
                </div>
                <div className="content-body">
                    <Link className="btn btn-link btn-sm mc-clear" to={from}>
                        <i className="bx bx-arrow-back" aria-hidden="true"/> Volver a Marcaciones
                    </Link>
                    {!employeeId ? (
                        <div className="mc-state mc-state--error" role="alert">
                            <i className="bx bx-error-circle" aria-hidden="true"/>
                            <p className="mc-state__title">Empleado no válido</p>
                            <p className="mc-state__text">El enlace no corresponde a un empleado. Vuelve a Marcaciones y elige uno de la lista.</p>
                        </div>
                    ) : (
                        <>
                            <div className="mc-panel mc-profile">
                                <div>
                                    <h2 className="mc-section__title">{title}</h2>
                                    {person && <p className="mc-person__meta">{[person.cargo, person.tienda, person.zona].filter(Boolean).join(' · ')}</p>}
                                </div>
                                <div className="mc-stepper mc-profile__month">
                                    <button type="button" className="mc-icon-button" aria-label="Mes anterior" onClick={() => goToMonth(-1)}>
                                        <i className="bx bx-chevron-left" aria-hidden="true"/>
                                    </button>
                                    <p className="mc-stepper__label" aria-live="polite">{monthLabel(month)}</p>
                                    <button type="button" className="mc-icon-button" aria-label="Mes siguiente"
                                            disabled={month >= today.slice(0, 7)} onClick={() => goToMonth(1)}>
                                        <i className="bx bx-chevron-right" aria-hidden="true"/>
                                    </button>
                                </div>
                                {canExport && <ExportButton token={token} params={{...params, seccion: 'jornadas'}} range={range}/>}
                            </div>

                            <h3 className="mc-panel__title">Indicadores del mes</h3>
                            <StateView status={dashboard.status} message={dashboard.message} onRetry={dashboard.reload}
                                       isEmpty={dashboard.status === 'ok' && dashboard.data.cantidad === 0}
                                       emptyTitle="Sin marcaciones en este mes" emptyHint="Usa las flechas para ver otro mes.">
                                {dashboard.status === 'ok' && (
                                    <div className="mc-kpis">
                                        {dashboard.data.datos.kpis.map((card) => <KpiCard key={card.clave} card={card}/>)}
                                    </div>
                                )}
                            </StateView>

                            <h3 className="mc-panel__title">Horas del mes</h3>
                            <StateView status={hours.status} message={hours.message} onRetry={hours.reload} isEmpty={weeks.length === 0}
                                       emptyTitle="Sin horas en este mes" emptyHint="Solo suman las jornadas completas.">
                                <div className="mc-tiles">
                                    <StatTile label="Horas trabajadas (total del mes)" value={`${formatNumber(totals.horas)} h`} hint="Jornadas completas"/>
                                    <StatTile label="Jornadas completas" value={totals.completas} hint={`${totals.sinAlmuerzo} sin almuerzo marcado`}/>
                                    <StatTile label="Incompletas" value={totals.incompletas} tone={totals.incompletas ? 'down' : 'neutral'} hint="No suman horas"/>
                                </div>
                                <div className="mc-panel">
                                    <ul className="mc-list">
                                        {weeks.map((week) => (
                                            <li key={week.periodo_inicio} className="mc-list__item">
                                                <span className="mc-person"><span className="mc-person__name">{groupLabel(week.periodo_inicio, 'semana')}</span></span>
                                                <span className="mc-list__time mc-num">{durationLabel(week.minutos_trabajados)}</span>
                                                <StatusBadge tone={week.jornadas_incompletas ? 'down' : 'muted'}
                                                             text={`${week.jornadas_completas} completas${week.jornadas_incompletas ? ` · ${week.jornadas_incompletas} incompletas` : ''}`}/>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </StateView>

                            <h3 className="mc-panel__title">Historial del mes</h3>
                            <StateView status={workdays.status} message={workdays.message} onRetry={workdays.reload}
                                       isEmpty={dayRows.length === 0} emptyTitle="Sin marcaciones en este mes"
                                       emptyHint="Usa las flechas para ver otro mes.">
                                <div className="mc-panel">
                                    <div className="mc-list__head" aria-hidden="true">
                                        <span>Día</span><span>Entrada</span><span>Llegada</span><TimelineAxis/>
                                    </div>
                                    <ul className="mc-list">
                                        {dayRows.map((row) => {
                                            const arrival = arrivalStatus(row);
                                            const exit = exitBadge(row);
                                            return (
                                                <li key={row.fecha} className="mc-list__item mc-list__item--timeline">
                                                    <span className="mc-person">
                                                        <span className="mc-person__name">{shortDayLabel(row.fecha)}</span>
                                                        <span className="mc-person__meta">Salida {hhmm(row.salida)} · {exit.text}</span>
                                                    </span>
                                                    <span className="mc-list__time mc-num">{hhmm(row.entrada)}</span>
                                                    <StatusBadge tone={arrival.tone} text={arrival.text}/>
                                                    <BarraJornada row={row} today={today}/>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            </StateView>
                        </>
                    )}
                </div>
            </div>
            <ToastContainer/>
        </div>
    );
};

export default EmployeeProfile;
